/**
 * Module AppRouter - Bộ điều phối Hash Router trung tâm của Workspace
 * Quản lý 7 màn hình độc lập:
 * 0. Workspace Homepage (#view-workspace-home) -> Route: #/home hoặc #/
 * 1. Google Sheets (#view-sheets)              -> Route: #/sheets
 * 2. Gmail Web (#view-email)                   -> Route: #/email
 * 3. Zalo Web (#view-zalo)                     -> Route: #/zalo
 * 4. CapCut Web Studio (#view-capcut)          -> Route: #/capcut
 * 5. Canva Video Design (#view-canva)          -> Route: #/canva
 * 6. Calendar / Caro (#view-calendar)         -> Route: #/calendar
 * 
 * Hỗ trợ:
 * - Hash Routing: Không bao giờ bị lỗi 404 khi nhấn F5 / reload trên Web tĩnh Cloudflare Pages
 * - Deep Linking: Tự động khôi phục đúng chương truyện hoặc đúng giây video đang xem dở
 * - Tương thích ngược toàn diện với các link chia sẻ cũ qua Query Parameter (?source=... hoặc ?mode=video)
 */
import { UrlService } from '../services/urlService.js';
import { AppearanceService } from '../services/appearanceService.js';
import { THEME_KEYS } from './themeManager.js';

export class AppRouter {
  /**
   * Khởi tạo bộ điều phối router
   * @param {Object} options
   * @param {import('./themeManager.js').ThemeManagerModule} options.themeManager
   * @param {import('./storyReader.js').StoryReaderModule} options.storyReader
   * @param {import('./video/videoPlayerEngine.js').VideoPlayerEngine} options.playerEngine
   * @param {import('./video/capcutController.js').CapcutController} options.capcutController
   * @param {import('./video/canvaController.js').CanvaController} options.canvaController
   * @param {Object} [options.homeController] Controller điều khiển Workspace Homepage
   * @param {import('./auth/authGate.js').AuthGate} [options.authGate] Cổng đăng nhập: chưa đăng nhập thì không mở app nào
   */
  constructor({ authGate = null, themeManager, storyReader, playerEngine, capcutController, canvaController, homeController = null, searchModal = null, calendarController = null, emulatorController = null, figmaController = null, vscodeController = null, adminController = null }) {
    this.authGate = authGate;
    this.themeManager = themeManager;
    this.storyReader = storyReader;
    this.playerEngine = playerEngine;
    this.capcutController = capcutController;
    this.canvaController = canvaController;
    this.homeController = homeController;
    this.searchModal = searchModal;
    this.calendarController = calendarController;
    this.emulatorController = emulatorController;
    this.figmaController = figmaController;
    this.vscodeController = vscodeController;
    this.adminController = adminController;

    // 10 Container View độc lập trong DOM
    this.viewHome = document.getElementById('view-workspace-home');
    this.viewSheets = document.getElementById('view-sheets');
    this.viewEmail = document.getElementById('view-email');
    this.viewZalo = document.getElementById('view-zalo');
    this.viewCapcut = document.getElementById('view-capcut');
    this.viewCanva = document.getElementById('view-canva');
    this.viewCalendar = document.getElementById('view-calendar');
    this.viewEmulator = document.getElementById('view-emulator');
    this.viewFigma = document.getElementById('view-figma');
    this.viewVscode = document.getElementById('view-vscode');
    this.viewAdmin = document.getElementById('view-admin');

    // Các nút tùy chọn trong Modal Chọn Giao diện
    this.optHome = document.getElementById('theme-opt-home');
    this.optSheets = document.getElementById('theme-opt-sheets');
    this.optEmail = document.getElementById('theme-opt-email');
    this.optZalo = document.getElementById('theme-opt-zalo');
    this.optCapcut = document.getElementById('theme-opt-capcut');
    this.optCanva = document.getElementById('theme-opt-canva');
    this.optEmulator = document.getElementById('theme-opt-emulator');
    this.optFigma = document.getElementById('theme-opt-figma');
    this.optVscode = document.getElementById('theme-opt-vscode');

    this.currentMode = 'home'; // 'home' | 'story' | 'video' | 'calendar' | 'emulator' | 'figma' | 'vscode'
    this.currentTheme = 'home';

    this.bindEvents();
  }

  /**
   * Khởi động và điều hướng màn hình dựa trên Hash và URL hiện tại
   */
  async route() {
    // 0. Chưa đăng nhập: không mở app nào (AuthGate sẽ gọi lại route() ngay sau khi đăng nhập)
    if (this.authGate && !this.authGate.isUnlocked()) return;

    // 1. Phân tích hash hiện tại trên thanh địa chỉ trình duyệt
    const hash = (window.location.hash || '').trim().toLowerCase();
    const routeParams = this.getRouteParams();

    if (hash.startsWith('#/calendar')) {
      UrlService.cleanAppQueryParams();
      await this.showCalendarMode(hash);
      return;
    }
    this.calendarController?.leaveView();

    // Trang quản trị: xem người đã đăng nhập (máy chủ chỉ trả dữ liệu cho admin)
    if (hash.startsWith('#/admin')) {
      UrlService.cleanAppQueryParams();
      await this.showAdminMode();
      return;
    }

    if (hash.startsWith('#/figma') || hash.startsWith('#/prototype')) {
      UrlService.cleanAppQueryParams();
      await this.showFigmaMode(routeParams);
      return;
    }

    if (hash.startsWith('#/emulator') || hash.startsWith('#/testing') || hash.startsWith('#/shorts')) {
      UrlService.cleanAppQueryParams();
      await this.showEmulatorMode(routeParams);
      return;
    }

    if (hash.startsWith('#/vscode') || hash.startsWith('#/code') || hash.startsWith('#/ide')) {
      UrlService.cleanAppQueryParams();
      await this.showVscodeMode(routeParams);
      return;
    }

    // 2. Trường hợp đường dẫn Hash rõ ràng: Luôn làm sạch query param cũ (như ?theme=zalo) để URL chuẩn chỉ
    if (hash.startsWith('#/canva')) {
      UrlService.cleanAppQueryParams();
      const vParams = UrlService.getVideoParams();
      const videoId = routeParams.videoId || vParams.videoId;
      const startTime = routeParams.time || vParams.time || 0;
      await this.showVideoMode('canva', { ...vParams, videoId, time: startTime });
      return;
    }
    if (hash.startsWith('#/capcut')) {
      UrlService.cleanAppQueryParams();
      const vParams = UrlService.getVideoParams();
      const videoId = routeParams.videoId || vParams.videoId;
      const startTime = routeParams.time || vParams.time || 0;
      await this.showVideoMode('capcut', { ...vParams, videoId, time: startTime });
      return;
    }
    if (hash.startsWith('#/sheets')) {
      UrlService.cleanAppQueryParams();
      this.showStoryMode(THEME_KEYS.SHEETS);
      this.handleStoryAutoLoad(routeParams);
      return;
    }
    if (hash.startsWith('#/email')) {
      UrlService.cleanAppQueryParams();
      this.showStoryMode(THEME_KEYS.EMAIL);
      this.handleStoryAutoLoad(routeParams);
      return;
    }
    if (hash.startsWith('#/zalo')) {
      UrlService.cleanAppQueryParams();
      this.showStoryMode(THEME_KEYS.ZALO);
      this.handleStoryAutoLoad(routeParams);
      return;
    }
    if (hash === '#/home' || hash === '#/') {
      UrlService.cleanAppQueryParams();
      this.showWorkspaceHome();
      return;
    }

    // 3. Tương thích ngược: Nếu không có hash nhưng URL có tham số query video
    if (UrlService.isVideoUrl()) {
      const videoParams = UrlService.getVideoParams();
      const targetTheme = (videoParams.theme === 'canva') ? 'canva' : 'capcut';
      console.log(`[Router] Phát hiện liên kết Video qua Query Param -> Chuyển hướng: ${targetTheme}`);
      await this.showVideoMode(targetTheme, videoParams);
      return;
    }

    // Tương thích link reader dạng query cũ (?theme=email|sheets|zalo).
    const queryTheme = new URLSearchParams(window.location.search).get('theme');
    if ([THEME_KEYS.SHEETS, THEME_KEYS.EMAIL, THEME_KEYS.ZALO].includes(queryTheme)) {
      this.showStoryMode(queryTheme);
      this.handleStoryAutoLoad(routeParams);
      return;
    }

    // 4. Tương thích ngược: Nếu URL có tham số đọc truyện (storyId & chapterId)
    if (routeParams.storyId && routeParams.chapterId) {
      const savedTheme = localStorage.getItem('fake_disguise_theme');
      const targetTheme = [THEME_KEYS.SHEETS, THEME_KEYS.EMAIL, THEME_KEYS.ZALO].includes(savedTheme)
        ? savedTheme
        : THEME_KEYS.SHEETS;
      console.log(`[Router] Phát hiện liên kết Đọc truyện qua Query Param -> Mở ${targetTheme}`);
      this.showStoryMode(targetTheme);
      this.handleStoryAutoLoad(routeParams);
      return;
    }

    // 5. Mặc định khi vào trang gốc (root URL sạch sẽ): Mở Workspace Homepage
    console.log('[Router] Mở Trang chủ Workspace Hub ngụy trang an toàn');
    this.showWorkspaceHome();
  }

  /**
   * Bóc tách tham số từ cả hash query (#/app?param=...) và location search (?param=...)
   * @returns {{ source: string, storyId: string|null, chapterId: string|null, videoId: string|null, time: number, q: string|null }}
   */
  getRouteParams() {
    try {
      const hash = window.location.hash || '';
      const qIndex = hash.indexOf('?');
      const hashParams = new URLSearchParams(qIndex !== -1 ? hash.substring(qIndex + 1) : '');
      const searchParams = new URLSearchParams(window.location.search);
      const getParam = (...keys) => {
        for (const key of keys) {
          const value = hashParams.get(key) || searchParams.get(key);
          if (value) return value;
        }
        return null;
      };

      return {
        source: getParam('source', 's') || 's1',
        storyId: getParam('story', 'story_id'),
        chapterId: getParam('chap', 'chapter', 'chapter_id'),
        videoId: getParam('v', 'video', 'yt'),
        time: parseFloat(getParam('t', 'time') || '0') || 0,
        q: getParam('q', 'search')
      };
    } catch (e) {
      return { source: 's1', storyId: null, chapterId: null, videoId: null, time: 0, q: null };
    }
  }

  /**
   * Tự động nạp chương truyện nếu route có chỉ định storyId và chapterId
   * @param {Object} params 
   */
  handleStoryAutoLoad(params) {
    if (!this.storyReader) return;

    if (params.storyId && params.chapterId) {
      // Chỉ nạp nếu chưa có hoặc khác chương hiện tại
      const cur = this.storyReader.currentStory;
      if (cur.storyId !== params.storyId || cur.chapterId !== params.chapterId) {
        this.storyReader.loadChapter(params.source || 's1', params.storyId, params.chapterId, false);
      }
    } else {
      // Kiểm tra xem hiện tại đã có truyện nào được nạp chưa
      const hasStory = Boolean(
        this.storyReader.lastLoadedData ||
        (this.storyReader.currentStory && this.storyReader.currentStory.storyId)
      );

      // Nếu chưa có truyện nào: Khởi tạo bảng tính an toàn và tự động mở modal tìm kiếm (mặc định tab Truyện)
      if (!hasStory) {
        this.storyReader.initEmptySheet();
        this.searchModal?.open('', 'story');
      }
    }
  }

  /**
   * Gắn các sự kiện điều hướng, phím tắt và lịch sử trình duyệt
   */
  bindEvents() {
    // Đóng modal theme khi click chọn
    const closeModal = () => {
      const modal = document.getElementById('theme-selector-modal');
      if (modal) modal.style.display = 'none';
    };

    // Chọn Trang chủ Workspace từ modal
    if (this.optHome) {
      this.optHome.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/home');
      });
    }

    // Chọn Google Sheets từ modal
    if (this.optSheets) {
      this.optSheets.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/sheets');
      });
    }

    // Chọn Gmail Web từ modal
    if (this.optEmail) {
      this.optEmail.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/email');
      });
    }

    // Chọn Zalo Web từ modal
    if (this.optZalo) {
      this.optZalo.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/zalo');
      });
    }

    // Chọn CapCut Web từ modal
    if (this.optCapcut) {
      this.optCapcut.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/capcut');
      });
    }

    // Chọn Canva Video từ modal
    if (this.optCanva) {
      this.optCanva.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/canva');
      });
    }

    // Chọn Android Studio Emulator từ modal
    if (this.optEmulator) {
      this.optEmulator.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/emulator');
      });
    }

    // Chọn Figma Prototype từ modal
    if (this.optFigma) {
      this.optFigma.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/figma');
      });
    }

    // Chọn Visual Studio Code Angular IDE từ modal
    if (this.optVscode) {
      this.optVscode.addEventListener('click', () => {
        closeModal();
        this.navigateTo('#/vscode');
      });
    }

    if (this.figmaController) {
      this.figmaController.onPanicExit = () => this.handlePanicExit();
    }

    // Lắng nghe nút chuyển đổi qua lại giữa CapCut và Canva từ Controller
    if (this.capcutController) {
      this.capcutController.onSwitchTheme = (theme) => this.navigateTo(`#/${theme}`);
      this.capcutController.onPanicExit = () => this.handlePanicExit();
    }
    if (this.canvaController) {
      this.canvaController.onSwitchTheme = (theme) => this.navigateTo(`#/${theme}`);
      this.canvaController.onPanicExit = () => this.handlePanicExit();
    }

    // -------------------------------------------------------------------------
    // CƠ CHẾ GOM SỰ KIỆN ĐIỀU HƯỚNG (DEBOUNCED ROUTER DISPATCH)
    // Ngăn chặn việc trình duyệt bắn cả 2 sự kiện 'popstate' và 'hashchange' đồng thời
    // khiến this.route() bị gọi 2 lần liên tiếp gây xung đột phá hủy player (Double Mounting).
    // -------------------------------------------------------------------------
    let routeRafId = null;
    const scheduleRoute = () => {
      if (routeRafId) {
        cancelAnimationFrame(routeRafId);
      }
      routeRafId = requestAnimationFrame(() => {
        routeRafId = null;
        this.route();
      });
    };

    // Lắng nghe sự kiện hashchange trên window (Trọng tâm của Hash Routing)
    window.addEventListener('hashchange', scheduleRoute);

    // Lắng nghe sự kiện Back/Forward của trình duyệt
    window.addEventListener('popstate', scheduleRoute);

    // -------------------------------------------------------------------------
    // BẮT SỰ KIỆN CLICK TOÀN CỤC CHO MỌI ICON & TEXT LINK "TRANG CHỦ" TRÊN CÁC APP
    // -------------------------------------------------------------------------
    document.addEventListener('click', (e) => {
      // 1. Kiểm tra phần tử có selector nhận diện trang chủ hoặc logo
      const homeTrigger = e.target.closest(
        '[data-nav="home"], .btn-go-home, .sheets-logo, .gmail-logo, .cc-logo, .cv-logo, #btn-home-sheets, #btn-home-gmail, #zalo-nav-home'
      );
      if (homeTrigger) {
        e.preventDefault();
        e.stopPropagation();
        this.navigateTo('#/home');
        return;
      }

      // 2. Kiểm tra text link hoặc button có chứa chữ "Trang chủ"
      const clickableText = e.target.closest('span, a, button, .menu-item, .gmail-menu-item, .zalo-nav-item, .cc-nav-item, .cv-nav-item');
      if (clickableText) {
        const text = (clickableText.textContent || '').trim().toLowerCase();
        // Nhận diện các biến thể: "trang chủ", "🏠 trang chủ", "về trang chủ"
        if (text === 'trang chủ' || text === '🏠 trang chủ' || text.startsWith('trang chủ') || text.includes('về trang chủ')) {
          // Bỏ qua nếu đang click trong modal theme hoặc trang chủ chính nó
          if (clickableText.closest('#theme-selector-modal')) {
            const modal = document.getElementById('theme-selector-modal');
            if (modal) modal.style.display = 'none';
            this.navigateTo('#/home');
            return;
          }
          if (clickableText.closest('#view-workspace-home')) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          this.navigateTo('#/home');
        }
      }
    });
  }

  /**
   * Điều hướng đến route hash chỉ định và dọn sạch các query param cũ trên URL
   * @param {string} hashRoute Ví dụ: '#/home', '#/sheets', '#/canva', '#/capcut'
   */
  navigateTo(hashRoute) {
    if (!hashRoute || typeof hashRoute !== 'string') return;
    const cleanHash = hashRoute.startsWith('#') ? hashRoute : `#${hashRoute}`;

    try {
      const currentUrl = new URL(window.location.href);
      let hasConflictingParams = false;
      const keysToRemove = ['theme', 'mode', 'v', 'video', 'videoId', 'yt', 'play', 't', 'time', 'vq'];
      
      keysToRemove.forEach(k => {
        if (currentUrl.searchParams.has(k)) {
          currentUrl.searchParams.delete(k);
          hasConflictingParams = true;
        }
      });

      // Nếu URL có query param cũ (ví dụ ?theme=zalo), dọn sạch và cập nhật URL sạch bằng pushState
      if (hasConflictingParams) {
        currentUrl.hash = cleanHash;
        window.history.pushState({ route: cleanHash }, '', currentUrl.toString());
        this.route();
        return;
      }
    } catch (err) {
      console.warn('[Router] Cảnh báo làm sạch URL khi navigateTo:', err);
    }

    // Nếu URL không có query param thừa, cập nhật hash bình thường
    window.location.hash = cleanHash;
  }

  /**
   * Ẩn toàn bộ các màn hình view container
   */
  hideAllViews() {
    const views = [
      this.viewHome,
      this.viewSheets,
      this.viewEmail,
      this.viewZalo,
      this.viewCapcut,
      this.viewCanva,
      this.viewCalendar,
      this.viewEmulator,
      this.viewFigma,
      this.viewVscode,
      this.viewAdmin
    ];
    views.forEach(v => {
      if (v) v.classList.remove('active');
    });
    // Vô hiệu hóa và ngắt âm thanh Android Emulator nếu rời màn hình
    if (this.currentTheme !== 'emulator') {
      this.emulatorController?.deactivate();
    }
    // Vô hiệu hóa và ngắt âm thanh Figma nếu rời màn hình
    if (this.currentTheme !== 'figma') {
      this.figmaController?.deactivate();
    }
    // Vô hiệu hóa trạng thái VS Code nếu rời màn hình
    if (this.currentTheme !== 'vscode') {
      this.vscodeController?.deactivate();
    }
    if (this.currentTheme !== 'admin') {
      this.adminController?.deactivate();
    }
  }

  /**
   * Chuyển sang trang quản trị người dùng (#/admin)
   */
  async showAdminMode() {
    this.currentMode = 'admin';
    this.currentTheme = 'admin';
    if (this.themeManager) this.themeManager.currentTheme = 'admin';
    if (this.playerEngine) this.playerEngine.emergencyMuteAndPause();
    if (this.storyReader?.ttsEngine?.isPlaying) this.storyReader.ttsEngine.pause();

    try { await this.adminController?.ensure?.(); } catch (e) { console.warn('[Router] Lazy load lỗi:', e); }
    this.hideAllViews();
    if (this.viewAdmin) this.viewAdmin.classList.add('active');
    document.title = 'Quản trị - Coogle Wordspeca';
    this.setWorkspaceFavicon();

    await this.adminController?.activate();
  }

  /**
   * Chuyển sang màn hình Visual Studio Code Angular IDE
   * @param {Object} [params={}] Tham số route (articleUrl, q)
   */
  async showVscodeMode(params = {}) {
    this.currentMode = 'vscode';
    this.currentTheme = 'vscode';
    if (this.themeManager) this.themeManager.currentTheme = 'vscode';
    if (this.playerEngine) this.playerEngine.emergencyMuteAndPause();
    if (this.storyReader?.ttsEngine?.isPlaying) this.storyReader.ttsEngine.pause();

    try { await this.vscodeController?.ensure?.(); } catch (e) { console.warn('[Router] Lazy load lỗi:', e); }
    this.hideAllViews();
    if (this.viewVscode) this.viewVscode.classList.add('active');

    // Cập nhật đồng bộ Favicon và Title tab trình duyệt chuẩn VS Code
    AppearanceService.updateAppearance('vscode', { videoTitle: params.title || '' });

    await this.vscodeController?.activate(params.articleUrl || params.url, params.q);
  }

  /**
   * Chuyển sang màn hình Figma Mobile Prototype Studio
   * @param {Object} [params={}] Tham số route (videoId, q)
   */
  async showFigmaMode(params = {}) {
    this.currentMode = 'figma';
    this.currentTheme = 'figma';
    if (this.themeManager) this.themeManager.currentTheme = 'figma';
    if (this.playerEngine) this.playerEngine.emergencyMuteAndPause();
    if (this.storyReader?.ttsEngine?.isPlaying) this.storyReader.ttsEngine.pause();

    try { await this.figmaController?.ensure?.(); } catch (e) { console.warn('[Router] Lazy load lỗi:', e); }
    this.hideAllViews();
    if (this.viewFigma) this.viewFigma.classList.add('active');

    // Cập nhật đồng bộ Favicon và Title tab trình duyệt chuẩn Figma
    AppearanceService.updateAppearance('figma', { videoTitle: params.title || '' });

    await this.figmaController?.activate(params.videoId, params.q);
  }

  /**
   * Chuyển sang màn hình Android Emulator / Mobile Testing Studio
   * @param {Object} [params={}] Tham số route (videoId, q)
   */
  async showEmulatorMode(params = {}) {
    this.currentMode = 'emulator';
    this.currentTheme = 'emulator';
    if (this.themeManager) this.themeManager.currentTheme = 'emulator';
    if (this.playerEngine) this.playerEngine.emergencyMuteAndPause();
    if (this.storyReader?.ttsEngine?.isPlaying) this.storyReader.ttsEngine.pause();

    try { await this.emulatorController?.ensure?.(); } catch (e) { console.warn('[Router] Lazy load lỗi:', e); }
    this.hideAllViews();
    if (this.viewEmulator) this.viewEmulator.classList.add('active');

    // Cập nhật đồng bộ Favicon và Title tab trình duyệt chuẩn Android Studio
    AppearanceService.updateAppearance('emulator', { videoTitle: params.title || '' });

    await this.emulatorController?.activate(params.videoId, params.q);
  }

  async showCalendarMode(hash) {
    this.currentMode = 'calendar';
    this.currentTheme = 'calendar';
    if (this.themeManager) this.themeManager.currentTheme = 'calendar';
    if (this.playerEngine) this.playerEngine.emergencyMuteAndPause();
    if (this.storyReader?.ttsEngine?.isPlaying) this.storyReader.ttsEngine.pause();
    try { await this.calendarController?.ensure?.(); } catch (e) { console.warn('[Router] Lazy load lỗi:', e); }
    this.hideAllViews();
    if (this.viewCalendar) this.viewCalendar.classList.add('active');
    document.title = 'Calendar · Caro';
    await this.calendarController?.showRoute(hash);
  }

  /**
   * Chuyển toàn bộ giao diện về màn hình Workspace Homepage
   */
  showWorkspaceHome() {
    this.currentMode = 'home';
    this.currentTheme = 'home';
    if (this.themeManager) {
      this.themeManager.currentTheme = THEME_KEYS.HOME;
    }

    // 1. Tạm dừng âm thanh video khẩn cấp nếu đang phát
    if (this.playerEngine) {
      this.playerEngine.emergencyMuteAndPause();
    }

    // 2. Tạm dừng đọc truyện TTS nếu đang phát
    if (this.storyReader?.ttsEngine?.isPlaying) {
      this.storyReader.ttsEngine.pause();
    }

    // 3. Ẩn tất cả view và kích hoạt view Workspace Home
    this.hideAllViews();
    if (this.viewHome) {
      this.viewHome.classList.add('active');
    }

    // 4. Cập nhật Title và Favicon cổng Coogle Wordspeca
    document.title = 'Coogle Wordspeca';
    this.setWorkspaceFavicon();

    // 4.1. Dọn query của app trước đó (theme, mode, video...) để URL Home sạch: /#/home
    try {
      const homeUrl = new URL(window.location.href);
      ['theme', 'mode', 'v', 'video', 'videoId', 'yt', 'play', 't', 'time', 'vq'].forEach(k => homeUrl.searchParams.delete(k));
      window.history.replaceState({ mode: 'home' }, '', homeUrl.toString());
    } catch (e) {}

    // 5. Yêu cầu HomeController làm mới lại Continue Card & Recent List
    if (this.homeController && typeof this.homeController.refresh === 'function') {
      this.homeController.refresh();
    }
  }

  /**
   * Chuyển toàn bộ giao diện sang chế độ Video (CapCut hoặc Canva)
   * @param {'capcut' | 'canva'} targetTheme 
   * @param {Object} [videoParams={}] 
   */
  async showVideoMode(targetTheme = 'capcut', videoParams = {}) {
    const engine = this.playerEngine;
    const isThemeSwitch = this.currentMode === 'video' && this.currentTheme !== targetTheme
      && engine?.currentVideoId
      && (!videoParams.videoId || videoParams.videoId === engine.currentVideoId);
    const videoId = videoParams.videoId || engine?.currentVideoId || null;
    // Đổi giao diện lấy vị trí trực tiếp từ player, không lấy t cũ trên URL.
    const startTime = isThemeSwitch ? engine.getCurrentTime() : (videoParams.time || 0);
    const autoPlay = isThemeSwitch ? engine.isPlaying : true;
    this.currentMode = 'video';
    this.currentTheme = targetTheme;

    // Tải lười giao diện CapCut/Canva (JS + CSS + HTML) trước khi mount player
    const lazyController = targetTheme === 'canva' ? this.canvaController : this.capcutController;
    try {
      await lazyController?.ensure?.();
    } catch (loadErr) {
      console.warn('[Router] Không tải được giao diện video:', loadErr);
    }

    // Lưu bền vững trạng thái theme vào localStorage
    try {
      localStorage.setItem('fake_disguise_theme', targetTheme);
      localStorage.setItem('fake_video_theme', targetTheme);
    } catch (e) {}

    // 1. CẬP NHẬT NGAY LẬP TỨC TITLE VÀ FAVICON TRÌNH DUYỆT (0ms)
    AppearanceService.updateAppearance(targetTheme, { videoTitle: videoParams.title || '' });

    // 1.1. Đồng bộ query trên URL (mode=video, theme=<đích>) để không còn theme cũ như ?theme=zalo
    UrlService.setVideoParams({
      videoId,
      theme: targetTheme,
      time: startTime
    }, false);

    // 2. Ẩn toàn bộ view và kích hoạt view video tương ứng
    this.hideAllViews();
    if (targetTheme === 'canva') {
      if (this.viewCanva) this.viewCanva.classList.add('active');
    } else {
      if (this.viewCapcut) this.viewCapcut.classList.add('active');
    }

    // 3. Mount Core Player vào khung tương ứng và phát video
    try {
      const targetContainerId = targetTheme === 'canva' ? 'cv-player-iframe' : 'cc-player-iframe';
      if (this.playerEngine && typeof this.playerEngine.mountTo === 'function') {
        // Nạp đúng video/vị trí một lần; không loadVideo lần hai về t=0.
        await this.playerEngine.mountTo(targetContainerId, videoId, startTime, autoPlay);
      } else if (videoId && this.playerEngine) {
        await this.playerEngine.loadVideo(videoId, startTime, autoPlay);
      }
    } catch (mountErr) {
      console.warn('[Router] Cảnh báo khi mount video player:', mountErr);
    }
  }

  /**
   * Chuyển toàn bộ giao diện về chế độ Đọc truyện an toàn (Sheets / Email / Zalo)
   * @param {string} [targetTheme='sheets'] 
   */
  showStoryMode(targetTheme = THEME_KEYS.SHEETS) {
    this.currentMode = 'story';
    this.currentTheme = targetTheme;

    // Lưu bền vững theme truyện vào localStorage
    try {
      localStorage.setItem('fake_disguise_theme', targetTheme);
    } catch (e) {}

    // 1. Tắt tiếng và tạm dừng video khẩn cấp
    if (this.playerEngine) {
      this.playerEngine.emergencyMuteAndPause();
    }

    // 2. Ẩn tất cả view và kích hoạt giao diện truyện tương ứng
    this.hideAllViews();
    if (this.themeManager) {
      if (typeof this.themeManager.applyTheme === 'function') {
        this.themeManager.applyTheme(targetTheme, false, false);
      } else if (typeof this.themeManager.setTheme === 'function') {
        this.themeManager.setTheme(targetTheme, false, false);
      }
    }
    if (this.storyReader?.lastLoadedData) {
      this.storyReader.renderToTheme(targetTheme, this.storyReader.lastLoadedData);
    }

    // 3. Giả mạo Title và Favicon của bộ ứng dụng văn phòng
    AppearanceService.updateAppearance(targetTheme, this.storyReader?.lastLoadedData);
  }

  /**
   * Xử lý phím thoát hiểm khẩn cấp (Boss Key): Ngắt âm thanh/video và ngụy trang an toàn
   */
  handlePanicExit() {
    console.log('[Boss Key] Kích hoạt thoát hiểm khẩn cấp!');
    if (this.playerEngine) {
      this.playerEngine.emergencyMuteAndPause();
    }
    if (this.storyReader?.ttsEngine?.isPlaying) {
      this.storyReader.ttsEngine.pause();
    }
    // Chuyển ngay về Google Sheets trắng an toàn
    this.navigateTo('#/sheets');
  }

  /**
   * Thiết lập favicon Coogle Wordspeca cho Homepage
   */
  setWorkspaceFavicon() {
    try {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.type = 'image/svg+xml';
        link.rel = 'shortcut icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect x='4' y='4' width='6' height='6' rx='1' fill='%231b8492'/%3E%3Cpath d='M16 4l3 6h-6z' fill='%23dc8735'/%3E%3Ccircle cx='25' cy='7' r='3' fill='%236952ba'/%3E%3Ccircle cx='7' cy='16' r='3' fill='%23c95962'/%3E%3Crect x='13' y='13' width='6' height='6' rx='1' fill='%23748c35'/%3E%3Cpath d='M25 13l3 6h-6z' fill='%232d78b7'/%3E%3Cpath d='M7 22l3 6H4z' fill='%239557a2'/%3E%3Ccircle cx='16' cy='25' r='3' fill='%23c47c29'/%3E%3Crect x='22' y='22' width='6' height='6' rx='1' fill='%23368c69'/%3E%3C/svg%3E";
    } catch (e) {}
  }
}
